import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const BarOperations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('table_service');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [availableTables, setAvailableTables] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  useEffect(() => {
    if (orderType === 'table_service') {
      fetchAvailableTables();
    }
  }, [orderType]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchCategories(), fetchMenuItems()]);
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const data = await apiGet('/admin/bar/menu/categories');
      if (data.success) {
        // Remove duplicates by filtering unique category IDs
        const uniqueCategories = data.data.filter((cat, index, self) =>
          index === self.findIndex((t) => t.id === cat.id)
        );
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const url = selectedCategory === 'all'
        ? '/admin/bar/menu/items'
        : `/admin/bar/menu/items?category_id=${selectedCategory}`;

      const data = await apiGet(url);
      if (data.success) setMenuItems(data.data);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const fetchAvailableTables = async () => {
    try {
      const data = await apiGet('/admin/pos/main/available-tables');
      if (data.success) {
        setAvailableTables(data.data);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const addToCart = (item) => {
    if (!item.is_available) {
      alert('This drink is currently unavailable!');
      return;
    }

    const existingItem = cart.find(cartItem => cartItem.item_id === item.id);
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.item_id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1, subtotal: (cartItem.quantity + 1) * cartItem.price }
          : cartItem
      ));
    } else {
      setCart([...cart, {
        item_id: item.id,
        item_name: item.item_name,
        price: parseFloat(item.price),
        quantity: 1,
        subtotal: parseFloat(item.price)
      }]);
    }
  };

  const updateQuantity = (item_id, change) => {
    setCart(cart.map(item => {
      if (item.item_id === item_id) {
        const newQuantity = Math.max(1, item.quantity + change);
        return { ...item, quantity: newQuantity, subtotal: newQuantity * item.price };
      }
      return item;
    }));
  };

  const removeFromCart = (item_id) => {
    setCart(cart.filter(item => item.item_id !== item_id));
  };

  const clearCart = () => {
    if (window.confirm('Clear all items from cart?')) {
      setCart([]);
    }
  };

  const calculateTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    const tax = subtotal * 0.12;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const downloadBill = async (orderId, orderNumber) => {
    try {
      const response = await apiGet('/admin/bar/bills/${orderId}/download');
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Bar_Bill_${orderNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading bill:', error);
      alert('Error downloading bill');
    }
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      alert('Cart is empty!');
      return;
    }

    // Validation for table service (table selection required)
    if (orderType === 'table_service' && !selectedTable) {
      alert('Please select a table for table service!');
      return;
    }

    // Validation for room service
    if (orderType === 'room_service' && !roomNumber) {
      alert('Please enter room number for room service!');
      return;
    }

    try {
      const orderData = {
        customer_name: customerName,
        customer_phone: customerPhone,
        order_type: orderType,
        items: cart,
        discount_amount: 0,
        payment_method: 'cash'
      };

      // Add table_id for table service orders
      if (orderType === 'table_service') {
        orderData.table_id = selectedTable;
      }

      // Add room_number for room service
      if (orderType === 'room_service') {
        orderData.room_number = roomNumber;
      }

      const response = await fetch('/admin/bar/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Bar Order ${data.data.order_number} placed successfully!`);

        // For individual orders, generate and download bill immediately
        if (orderType === 'individual') {
          setTimeout(() => {
            downloadBill(data.data.order_id, data.data.order_number);
          }, 500);
        }

        // Reset form
        setCart([]);
        setCustomerName('');
        setCustomerPhone('');
        setRoomNumber('');
        setSelectedTable('');

        // Refresh available tables
        if (orderType === 'table_service') {
          fetchAvailableTables();
        }
      }
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Error placing order');
    }
  };

  const { subtotal, tax, total } = calculateTotal();

  const styles = {
    container: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
      gap: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    filterContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      overflowX: 'auto',
      paddingBottom: '4px'
    },
    filterButton: {
      padding: isMobile ? '8px 12px' : '10px 16px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      background: colors.white,
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap'
    },
    menuGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '10px' : '12px'
    },
    menuItem: {
      padding: isMobile ? '12px' : '14px',
      borderRadius: '10px',
      border: `2px solid ${colors.mediumGray}`,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.white,
      position: 'relative'
    },
    unavailableItem: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    unavailableBadge: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      background: colors.danger,
      color: colors.white,
      fontSize: '9px',
      padding: '2px 6px',
      borderRadius: '4px',
      fontWeight: '700'
    },
    cartItem: {
      padding: '12px',
      background: colors.lightGray,
      borderRadius: '8px',
      marginBottom: '8px'
    },
    select: {
      width: '100%',
      padding: '10px',
      marginBottom: '12px',
      borderRadius: '8px',
      border: `2px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    input: {
      width: '100%',
      padding: '10px',
      marginBottom: '12px',
      borderRadius: '8px',
      border: `2px solid ${colors.mediumGray}`,
      fontSize: '13px',
      boxSizing: 'border-box'
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    primaryButton: {
      background: colors.primary,
      color: colors.white,
      width: '100%'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading Bar POS...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.container}>
        {/* Bar Menu Section */}
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}><span>🍸</span> Bar Menu</h2>

          <div style={styles.filterContainer}>
            <button
              onClick={() => setSelectedCategory('all')}
              style={{
                ...styles.filterButton,
                background: selectedCategory === 'all' ? colors.primary : colors.white,
                color: selectedCategory === 'all' ? colors.white : colors.textDark,
                borderColor: selectedCategory === 'all' ? colors.primary : colors.mediumGray
              }}
            >
              All Drinks
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  ...styles.filterButton,
                  background: selectedCategory === cat.id ? colors.primary : colors.white,
                  color: selectedCategory === cat.id ? colors.white : colors.textDark,
                  borderColor: selectedCategory === cat.id ? colors.primary : colors.mediumGray
                }}
              >
                {cat.icon && `${cat.icon} `}{cat.category_name}
              </button>
            ))}
          </div>

          {menuItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No drinks available
            </div>
          ) : (
            <div style={styles.menuGrid}>
              {menuItems.map(item => (
                <div
                  key={item.id}
                  style={{
                    ...styles.menuItem,
                    ...(item.is_available ? {} : styles.unavailableItem)
                  }}
                  onClick={() => addToCart(item)}
                  onMouseEnter={(e) => !isMobile && item.is_available && (e.currentTarget.style.borderColor = colors.primary)}
                  onMouseLeave={(e) => !isMobile && (e.currentTarget.style.borderColor = colors.mediumGray)}
                >
                  {!item.is_available && (
                    <div style={styles.unavailableBadge}>
                      ✕ Unavailable
                    </div>
                  )}

                  <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '700', marginBottom: '4px', color: colors.textDark }}>
                    {item.item_name}
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '6px' }}>
                    {item.category_name}
                  </div>
                  {item.alcohol_content > 0 && (
                    <div style={{ fontSize: '10px', color: colors.warning, marginBottom: '4px' }}>
                      🥃 {item.alcohol_content}% ABV
                    </div>
                  )}
                  {item.volume_ml && (
                    <div style={{ fontSize: '10px', color: colors.textLight, marginBottom: '6px' }}>
                      📏 {item.volume_ml}ml
                    </div>
                  )}
                  <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', color: colors.primary }}>
                    ₹{parseFloat(item.price).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart */}
        <div style={styles.contentCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 0 }}>
              <span>🛒</span> Cart
            </h2>
            {cart.length > 0 && (
              <button
                onClick={clearCart}
                style={{
                  ...styles.button,
                  background: colors.danger,
                  color: colors.white,
                  fontSize: '11px',
                  padding: '6px 12px'
                }}
              >
                🗑️ Clear
              </button>
            )}
          </div>

          {/* Order Type Selection */}
          <select value={orderType} onChange={(e) => setOrderType(e.target.value)} style={styles.select}>
            <option value="table_service">🍹 Table Service</option>
            <option value="room_service">🏨 Room Service</option>
            <option value="individual">👤 Individual</option>
          </select>

          {/* Table Selection for Table Service */}
          {orderType === 'table_service' && (
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              style={{ ...styles.select, borderColor: !selectedTable && cart.length > 0 ? colors.danger : colors.mediumGray }}
            >
              <option value="">Select Table *</option>
              {availableTables.map(table => (
                <option key={table.id} value={table.id}>
                  Table {table.table_number} (Floor {table.floor}) - Capacity: {table.capacity}
                </option>
              ))}
            </select>
          )}

          {/* Room Number Input for Room Service */}
          {orderType === 'room_service' && (
            <input
              type="text"
              placeholder="Room Number *"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              style={{ ...styles.input, borderColor: !roomNumber && cart.length > 0 ? colors.danger : colors.mediumGray }}
            />
          )}

          {/* Customer Details */}
          <input
            type="text"
            placeholder="Customer Name (optional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            style={styles.input}
          />

          <input
            type="tel"
            placeholder="Customer Phone (optional)"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            style={styles.input}
          />

          {cart.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              Cart is empty
            </div>
          ) : (
            <>
              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
                {cart.map(item => (
                  <div key={item.item_id} style={styles.cartItem}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: colors.textDark }}>
                        {item.item_name}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.item_id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: colors.danger,
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => updateQuantity(item.item_id, -1)}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            border: `1px solid ${colors.mediumGray}`,
                            background: colors.white,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '700'
                          }}
                        >
                          -
                        </button>
                        <span style={{ fontSize: '14px', fontWeight: '600', minWidth: '20px', textAlign: 'center' }}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.item_id, 1)}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            border: `1px solid ${colors.mediumGray}`,
                            background: colors.white,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '700'
                          }}
                        >
                          +
                        </button>
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: colors.primary }}>
                        ₹{item.subtotal.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: `2px solid ${colors.mediumGray}`, paddingTop: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                  <span>Subtotal:</span>
                  <span style={{ fontWeight: '600' }}>₹{subtotal.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', color: colors.textLight }}>
                  <span>Tax (12%):</span>
                  <span>₹{tax.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: colors.primary }}>
                  <span>Total:</span>
                  <span>₹{total.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={handlePlaceOrder}
                style={{ ...styles.button, ...styles.primaryButton }}
              >
                {orderType === 'individual' ? '📄 Place Order & Generate Bill' : '📋 Place Order'}
              </button>

              {orderType === 'individual' && (
                <div style={{ fontSize: '11px', color: colors.textLight, textAlign: 'center', marginTop: '8px' }}>
                  Bill will be automatically downloaded
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default BarOperations;